import type { ProviderRole } from '../types';

export type ProviderMode = 'roles' | 'none';

/** Surgical billing modes — toggled via radio in the SURGICAL provider panel. */
export type BillingMode = 'standard' | 'cosurgery';

/**
 * Which providers a role accepts, by credential (feature 044). Omitted means "any
 * provider". Declared here rather than switched on in the panel so eligibility is a
 * property of the role, not a special case in a component.
 */
export type RoleEligibility = 'crna' | 'nonCrna';

export interface RoleDefinition {
  code: ProviderRole;
  label: string;
  description: string;
  /** Credential restriction on this role's picker. Unset = every provider is eligible. */
  eligibility?: RoleEligibility;
  /** True when the role accepts more than one provider on the same claim (feature 044). */
  multiple?: boolean;
}

/**
 * Maps each billing mode to the role codes shown for it. The role codes must
 * exist in the claim type's `roles` list (used for label/icon lookup).
 */
export interface BillingModeConfig {
  standard: ProviderRole[];
  cosurgery: ProviderRole[];
}

export interface ClaimTypeProviderConfig {
  mode: ProviderMode;
  roles?: RoleDefinition[];
  /** When set, the panel renders a Standard/Co-surgery radio toggle (SURGICAL). */
  billingModes?: BillingModeConfig;
  label: string;
  description: string;
}

const DEFAULT_PROVIDER_ROLE: RoleDefinition = Object.freeze({
  code: 'DEFAULT',
  label: 'Provider',
  description: 'Provider billing for this claim.',
});

/**
 * Claim type → provider UI mapping. Every selectable claim type has a `roles`
 * array; FACILITY has `mode: 'none'` and no roles (selected via click-toggle).
 */
export const CLAIM_TYPE_PROVIDER_CONFIG: Record<string, ClaimTypeProviderConfig> = {
  OFFICE_VISIT: {
    mode: 'roles',
    label: 'Single Office Visit',
    description: 'Submit one superbill for an office visit.',
    roles: [DEFAULT_PROVIDER_ROLE],
  },
  OFFICE_PROCEDURE: {
    mode: 'roles',
    label: 'Office Procedure',
    description: 'Claims for procedures performed in-office.',
    roles: [DEFAULT_PROVIDER_ROLE],
  },
  HOSPITAL_CONSULT: {
    mode: 'roles',
    label: 'Hospital Consult',
    description: 'A claim for visits or consultations provided by a doctor or specialist.',
    roles: [DEFAULT_PROVIDER_ROLE],
  },
  PATHOLOGY: {
    mode: 'roles',
    label: 'Pathology Claim',
    description: 'Claims for pathology and laboratory diagnostic services.',
    roles: [DEFAULT_PROVIDER_ROLE],
  },
  NEUROMONITORING: {
    mode: 'roles',
    label: 'Neuromonitoring Claim',
    description: 'Neuromonitoring services provided during a surgical procedure.',
    roles: [DEFAULT_PROVIDER_ROLE],
  },
  ER_CONSULT: {
    mode: 'roles',
    label: 'Consult + Procedure Same Admission',
    description: 'A consultation and a procedure performed during the same hospital admission.',
    roles: [DEFAULT_PROVIDER_ROLE],
  },
  // Both anesthesia roles are always shown and always optional; eligibility is decided
  // by the provider's credential, not by what the other role holds (feature 044).
  ANESTHESIA: {
    mode: 'roles',
    label: 'Anesthesia Claim',
    description: 'Anesthesia services provided during a procedure or surgery.',
    roles: [
      {
        code: 'ANESTHESIOLOGIST',
        label: 'Anesthesiologist',
        description: 'Any provider who is not a CRNA',
        eligibility: 'nonCrna',
        multiple: true,
      },
      {
        code: 'CRNA',
        label: 'CRNA',
        description: 'Certified registered nurse anesthetist',
        eligibility: 'crna',
        multiple: true,
      },
    ],
  },
  SURGICAL: {
    mode: 'roles',
    label: 'Surgical Claim',
    description: 'Used for operations or surgical procedures.',
    // Flat list of every role the two modes can use — kept for label/icon lookup
    // (e.g. claim-type summaries). The visible fields are chosen by `billingModes`.
    roles: [
      { code: 'PRIMARY_SURGEON', label: 'Primary Surgeon', description: 'Single surgeon operating' },
      { code: 'ASSISTANT_SURGEON', label: 'Assist or PA', description: 'Assistant surgeon or physician assistant' },
      { code: 'PHYSICIANS_ASSISTANT', label: 'Physicians Assistant', description: 'Assisting PA' },
      { code: 'CO_SURGEON_1', label: 'Co-Surgeon 1', description: 'First of two co-surgeons' },
      { code: 'CO_SURGEON_2', label: 'Co-Surgeon 2', description: 'Second of two co-surgeons' },
    ],
    billingModes: {
      standard: ['PRIMARY_SURGEON', 'ASSISTANT_SURGEON'],
      cosurgery: ['CO_SURGEON_1', 'CO_SURGEON_2', 'PHYSICIANS_ASSISTANT'],
    },
  },
  // FACILITY is last — it has no providers/billing, so it belongs at the end of the
  // claim-type tiles and the stacked bill-type cards (portal submit/edit).
  FACILITY: {
    mode: 'none',
    label: 'Facility Claim (UB-04)',
    description: 'Claims (UB-04) billed by hospitals or surgery centers.',
  },
};

export function getProviderMode(claimType: string): ProviderMode {
  return CLAIM_TYPE_PROVIDER_CONFIG[claimType]?.mode ?? 'none';
}

export function getProviderRoles(claimType: string): RoleDefinition[] {
  return CLAIM_TYPE_PROVIDER_CONFIG[claimType]?.roles ?? [];
}

export function getBillingModes(claimType: string): BillingModeConfig | undefined {
  return CLAIM_TYPE_PROVIDER_CONFIG[claimType]?.billingModes;
}

